import { Button, Spinner, Text, makeStyles, tokens } from '@coreai/fluentui-react';
import { ConnectorIcon } from '@/components/ConnectionsView/ConnectorIcon';
import { getNodeActionName } from '@/hooks/useNodeConnectorInfo';
import { useNodeAboutMetadata } from '@/hooks/useNodeAboutMetadata';
import type { SelectedNode } from '@/stores/nodeEditorStore';
import { NodeAboutCard, NodeAboutDocumentationLink } from './NodeAboutShared';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, minWidth: 0, maxWidth: '100%' },
  details: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, margin: 0 },
  row: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: 0 },
  label: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  value: { margin: 0, color: tokens.colorNeutralForeground1, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' },
  status: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
});

interface NodeAboutTabProps {
  node: SelectedNode;
  parameterValues: Record<string, unknown>;
}

export function NodeAboutTab({ node, parameterValues }: NodeAboutTabProps) {
  const styles = useStyles();
  const metadata = useNodeAboutMetadata(node, parameterValues);
  const isLoading = metadata.isLoading && !metadata.error;
  const missing = isLoading ? 'Loading...' : 'Unavailable';
  const rows = [
    [node.type === 'trigger' ? 'Workflow trigger name' : 'Workflow action name', getNodeActionName(node)],
    ...(node.type === 'tool' ? [['Tool name', node.toolName ?? node.name]] : []),
    ...(node.parentAgentName ? [['Parent agent', node.parentAgentName]] : []),
    ['Connector type', metadata.connectorType ?? missing],
    ['Connector name', metadata.connectorName || missing],
    ['Operation name', metadata.operationName || missing],
    ['Description', metadata.description || (isLoading ? 'Loading...' : 'No operation description available.')],
  ];

  return (
    <div className={styles.root} data-testid="node-about-metadata" aria-busy={isLoading}>
      <NodeAboutCard name={getNodeActionName(node)} subtitle={metadata.connectorName || missing}
        icon={<ConnectorIcon key={metadata.iconUri} size={48}
          {...(metadata.iconUri && { iconUri: metadata.iconUri })}
          {...(metadata.brandColor && { brandColor: metadata.brandColor })} />} />
      <div>
        <div role="status" aria-label="Operation metadata">
          {isLoading && <Spinner size="tiny" label="Loading operation metadata" />}
          {!metadata.error && !isLoading && (!metadata.connectorName || !metadata.operationName) && (
            <Text>Some metadata is unavailable. Check the action's connection or reopen the editor.</Text>
          )}
        </div>
        <div role="alert" className={styles.status}>
          {metadata.error && (
            <>
              <Text>Unable to load operation metadata.</Text>
              <Button appearance="subtle" onClick={() => void metadata.retry()}>Retry</Button>
            </>
          )}
        </div>
      </div>
      <dl className={styles.details}>
        {rows.map(([label, value]) => (
          <div className={styles.row} key={label}>
            <dt className={styles.label}>{label}</dt>
            <dd className={styles.value}>{value}</dd>
          </div>
        ))}
      </dl>
      <NodeAboutDocumentationLink />
    </div>
  );
}
